// Ported verbatim from
// files/snapdraw-design-kit/code/src/components/snapdraw/language-toggle.tsx
// (Lovable reference implementation) — pure presentation, no changes needed.
import { Languages } from "lucide-react";

import { LANGUAGES, useI18n } from "@/lib/i18n";
import { cn } from "@/lib/utils";

/**
 * Segmented EN / ES switch. `variant="pill"` is the compact chrome version used
 * in the app header and auth screens; `variant="block"` is the full-width
 * control used on the Account settings page.
 */
export function LanguageToggle({
  variant = "pill",
  className,
}: {
  variant?: "pill" | "block";
  className?: string;
}) {
  const { lang, setLang } = useI18n();
  const block = variant === "block";

  return (
    <div
      role="group"
      aria-label="Language"
      className={cn(
        "inline-flex items-center gap-1 rounded-xl border border-border bg-black/30 p-1",
        block && "w-full",
        className,
      )}
    >
      {!block && <Languages className="ml-2 mr-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />}
      {LANGUAGES.map((l) => {
        const active = lang === l.code;
        return (
          <button
            key={l.code}
            type="button"
            lang={l.code}
            aria-pressed={active}
            onClick={() => setLang(l.code)}
            className={cn(
              "rounded-lg font-mono uppercase tracking-[0.16em] transition-colors",
              // Not currently visibly broken here -- "English"/"Español" are
              // the same length, so flex-1's default min-width:auto floor
              // never gets exercised -- but it's the identical structure
              // that did overflow on ThemeToggle's block variant (backlog
              // MOBILE-24 follow-up: "System" pushed past the group's right
              // edge). min-w-0 here is preventive, not a reaction to an
              // observed break, so this doesn't quietly regress the moment
              // a longer label (a third language, a longer translation)
              // makes the same imbalance real.
              block ? "flex-1 min-w-0 px-4 py-3 text-xs" : "px-2.5 py-1.5 text-[10px] lg:px-3",
              active ? "bg-arc text-primary-foreground" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {block ? l.native : l.label}
          </button>
        );
      })}
    </div>
  );
}
